import { LegacyBalanceTransactionDrawer } from '@src/containers/Dealer/CustomerBalances/LegacyBalanceTransactionDrawer';
import {
  AccordionGroup,
  AppwideDrawerContentType,
  CustomerLedgerUpdateTypeEnum,
  IconName,
  LookbackOption,
  ROLE,
  useAppwideDrawerContext,
  useDynamicCallback,
  type BlotterColumnState,
  type TabProps,
  type Transfer,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams, MenuItemDef } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useRoleAuth } from '../../../hooks';
import { FilteredTransfers, type RecentTransfersParams } from './FilteredTransfers';
import type { TransfersBlotterFilter } from './useTransfersFilter';

export interface TransfersTabProps extends TabProps {
  defaultFilter?: Partial<TransfersBlotterFilter>;
  defaultColumns?: BlotterColumnState[];
}

export function FilteredTransfersWrapper({
  tabId,
  blotterID,
  initialIsOpen,
  defaultColumns,
  defaultFilter,
  onCloneTab: handleCloneTab,
}: RecentTransfersParams & { tabId: string }) {
  const { isRole } = useRoleAuth();
  const canCreateBalanceTransaction = isRole(ROLE.TALOS_SUPPORT) || isRole(ROLE.DEALER);

  const { openDrawer: openBalanceTransactionDrawer, closeDrawer: closeBalanceTransactionDrawer } =
    useAppwideDrawerContext();

  // reset form state when opening new
  const openNewBalanceTransactionDrawer = useCallback(
    (transfer?: Transfer) => {
      openBalanceTransactionDrawer({
        renderContent: () => (
          <LegacyBalanceTransactionDrawer
            entity={transfer}
            isOpen={true}
            onClose={closeBalanceTransactionDrawer}
            transactionType={CustomerLedgerUpdateTypeEnum.Deposit}
            suppressHeader={true}
          />
        ),
        type: AppwideDrawerContentType.CreateCustomerDeposit,
        title: 'New Customer Deposit',
      });
    },
    [closeBalanceTransactionDrawer, openBalanceTransactionDrawer]
  );

  const newTabDefaults = useMemo(() => {
    return {
      defaultFilter: {
        _start: LookbackOption.Past30Days,
      },
      defaultColumns,
    } satisfies TransfersTabProps;
  }, [defaultColumns]);

  const getContextMenuItems = useDynamicCallback((params: GetContextMenuItemsParams<Transfer>) => {
    const transferMenuItems: MenuItemDef[] = [];
    if (canCreateBalanceTransaction) {
      const transfer = params?.node?.data;
      if (transfer != null && !transfer.ParentTransactionID && !transfer?.isTransferOutgoing) {
        transferMenuItems.push({
          name: 'Create Customer Deposit',
          action: () => {
            openNewBalanceTransactionDrawer(transfer);
          },
          icon: `<i class="ag-icon ${IconName.DocumentDownload}"/>`,
        });
      }
    }
    return compact([...transferMenuItems]);
  });

  return (
    <>
      <AccordionGroup key={tabId}>
        <FilteredTransfers
          key={tabId}
          blotterID={blotterID}
          defaultColumns={defaultColumns ?? newTabDefaults.defaultColumns}
          defaultFilter={defaultFilter ?? newTabDefaults.defaultFilter}
          onCloneTab={handleCloneTab}
          initialIsOpen={initialIsOpen}
          getContextMenuItems={getContextMenuItems}
        />
      </AccordionGroup>
    </>
  );
}
